/**
 * EditProfileDrawer — Side-sliding edit form for the user's own profile.
 *
 * Conditional fields:
 *   - Display Name: shown only if `displayEdit` permission is true
 *   - Groups: shown only if `groupEdit` permission is true
 *   - Email: always shown (every user can edit their email)
 *
 * @param {{
 *   open: boolean,
 *   onClose: () => void,
 *   onSave: (data: { email, display_name?, group_ids? }) => void,
 *   saving: boolean,
 *   email: string,
 *   displayName: string,
 *   groups: number[],
 *   groupData: Array<{ group_id: number, group_name: string }>,
 *   displayEdit: boolean,
 *   groupEdit: boolean,
 * }} props
 */

import { useState, useEffect, useRef, useCallback, useMemo } from 'react';

const EditProfileDrawer = ({
  open, onClose, onSave, saving,
  email: initEmail, displayName: initDisplayName,
  groups: initGroups, groupData, displayEdit, groupEdit,
}) => {
  const [email, setEmail] = useState('');
  const [displayName, setDisplayName] = useState('');
  const [selectedGroups, setSelectedGroups] = useState([]);
  const [errors, setErrors] = useState({});

  // Populate form from props only when drawer opens (false -> true)
  const prevOpen = useRef(false);
  useEffect(() => {
    if (open && !prevOpen.current) {
      setEmail(initEmail || '');
      setDisplayName(initDisplayName || '');
      setSelectedGroups(initGroups ? [...initGroups] : []);
      setErrors({});
    }
    prevOpen.current = open;
  });

  // Sort groups alphabetically
  const sortedGroups = useMemo(() => {
    if (!groupData || groupData.length === 0) return [];
    return [...groupData].sort((a, b) =>
      (a.group_name || '').localeCompare(b.group_name || '')
    );
  }, [groupData]);

  // Toggle a group checkbox
  const handleGroupToggle = useCallback((groupId) => {
    setSelectedGroups((prev) =>
      prev.includes(groupId)
        ? prev.filter((id) => id !== groupId)
        : [...prev, groupId]
    );
  }, []);

  // Validation
  const validate = useCallback(() => {
    const errs = {};

    // Email is always required
    if (!email.trim()) {
      errs.email = 'Email is required.';
    } else if (!/^[A-Za-z0-9._%+\-]+@[A-Za-z0-9.\-]+\.[A-Za-z]{2,}$/.test(email.trim())) {
      errs.email = 'Please enter a valid email address.';
    }

    // Display name required if editable
    if (displayEdit) {
      if (!displayName.trim()) {
        errs.displayName = 'Display name is required.';
      } else if (!/^[ -~]+$/.test(displayName.trim())) {
        errs.displayName = 'Only printable characters allowed.';
      }
    }

    setErrors(errs);
    return Object.keys(errs).length === 0;
  }, [email, displayName, displayEdit]);

  const handleSubmit = useCallback((e) => {
    e.preventDefault();
    if (!validate()) return;
    onSave({
      email: email.trim(),
      display_name: displayName.trim(),
      group_ids: selectedGroups,
    });
  }, [validate, onSave, email, displayName, selectedGroups]);

  return (
    <div className={`dp-drawer${open ? ' is-open' : ''}`}>
      <div className="dp-drawer__header">
        <h2 className="dp-drawer__title">Edit Profile</h2>
        <button className="dp-drawer__close" onClick={onClose}>&#x2715;</button>
      </div>

      <form className="dp-drawer__body" onSubmit={handleSubmit}>
        {/* Display Name (conditional) */}
        {displayEdit ? (
          <div className="dp-drawer__field">
            <label className="dp-drawer__label">
              Display Name <span className="dp-drawer__required">*</span>
            </label>
            <input
              type="text"
              className={`dp-drawer__input${errors.displayName ? ' is-invalid' : ''}`}
              value={displayName}
              onChange={(e) => setDisplayName(e.target.value)}
              placeholder="Your display name"
              autoFocus
            />
            {errors.displayName && <p className="dp-drawer__error">{errors.displayName}</p>}
          </div>
        ) : null}

        {/* Email (always shown) */}
        <div className="dp-drawer__field">
          <label className="dp-drawer__label">
            Email <span className="dp-drawer__required">*</span>
          </label>
          <input
            type="text"
            className={`dp-drawer__input${errors.email ? ' is-invalid' : ''}`}
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            placeholder="your@email.com"
            autoFocus={!displayEdit}
          />
          {errors.email && <p className="dp-drawer__error">{errors.email}</p>}
        </div>

        {/* Groups (conditional) */}
        {groupEdit && sortedGroups.length > 0 ? (
          <div className="dp-drawer__field">
            <label className="dp-drawer__label">Groups</label>
            <div className="mpp-group-list">
              {sortedGroups.map((group) => (
                <label key={group.group_id} className="mpp-group-item">
                  <input
                    type="checkbox"
                    checked={selectedGroups.includes(group.group_id)}
                    onChange={() => handleGroupToggle(group.group_id)}
                  />
                  {group.group_name}
                </label>
              ))}
            </div>
          </div>
        ) : null}

        {/* Footer */}
        <div className="dp-drawer__footer">
          <button type="button" className="dp-btn dp-btn--cancel" onClick={onClose} disabled={saving}>
            Cancel
          </button>
          <button type="submit" className="dp-btn dp-btn--primary" disabled={saving}>
            {saving ? 'Saving\u2026' : 'Save Changes'}
          </button>
        </div>
      </form>
    </div>
  );
};

export default EditProfileDrawer;
